/**
 *
 * WCCOfferCardCTA
 *
 */

import React from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import {
  IntlPropType,
  trackConversionInteraction,
} from "root/libs/core-libs/src";
import { DhlLink } from "@dhl-official/react-library";
import { Sizes } from "@dhl-official/stencil-library";
import { offerPurchaseMethods, P2PLinkTypes } from "root/libs/constants/src";
import { CanaryVersions } from "root/libs/ui-containers/src";
import { loadExpressDataRequest } from "one-time-shipment/src/containers/Express/actions";
import styled from "styled-components";

const StyledLink = styled(DhlLink)`
  & > dhl-button > .styled.md:is(button, a) {
    display: block;
    line-height: normal !important;
    text-align: center;
    text-decoration: none;
    min-height: auto !important;
    width: 100%;
    position: relative;
    font-weight: 700;
    padding: var(--gaq-size-space-9px) var(--gaq-size-space-5px) !important;
    border-radius: var(--dui-size-radius-md);
    font-size: 1.4rem !important;
    min-width: unset;
    height: unset !important;

    &:hover {
      color: var(--dui-color-white-500) !important;
    }
  }

  & > dhl-button > .styled.md.outline:is(button, a) {
    &:hover {
      color: var(--dui-color-red-500) !important;
    }
  }

  &.highlighted > dhl-button > .styled.md:is(button, a) {
    color: var(--dui-color-black-500) !important;
    background-color: var(--dui-color-yellow-500) !important;
    padding-top: var(--dui-size-space-8x);
    padding-bottom: var(--dui-size-space-8x);
    &:hover {
      color: var(--dui-color-black-500) !important;
      background-color: var(--dui-color-yellow-500) !important;
    }
    &:active {
      background-color: var(--dui-color-yellow-500) !important;
    }
  }
`;

const COPY_ID_PREFIX = "WCCOfferCardCTA";
const CARD_AND_SUMMARY_COPY_ID_PREFIX = "CardAndSummary";

const {
  constants: { TYPES_OF_CANARY_VERSIONS },
} = CanaryVersions;

function WCCOfferCardCTA({
  purchaseMethod,
  displayOffer,
  intl,
  offerIndex,
  isHighlighted,
  variationType,
  utfDataObject,
  canaryVersion,
  loadExpressData,
  documentsOptionValue,
  toggleFreightP2PModal,
}) {
  const { supportContactInformation } = displayOffer;

  function getCopy(id, values = {}) {
    return intl.formatMessage({ id }, values);
  }

  function nullChecker(id) {
    return !!intl.messages[`${CARD_AND_SUMMARY_COPY_ID_PREFIX}.${id}`];
  }

  function onClickHandler(event) {
    // Check if this is Express3 canary and REDBOX_EXPRESS offer type
    const isExpress3 = canaryVersion === TYPES_OF_CANARY_VERSIONS.express3;
    const isRedboxExpress =
      displayOffer.pathToPurchaseLinkType === P2PLinkTypes.REDBOX_EXPRESS;
    const isModalFreight =
      displayOffer.pathToPurchaseLinkType === P2PLinkTypes.MODAL_FREIGHT;

    if (isExpress3 && isRedboxExpress) {
      event.preventDefault();

      // Determine package type from documents option
      const packageType =
        documentsOptionValue === "true" ? "document" : "package";

      // Load Express with data
      loadExpressData(displayOffer.pathToPurchaseLink, packageType);
    } else if (isModalFreight && toggleFreightP2PModal) {
      event.preventDefault();
      // Pass the entire offer object (it already has freightModal property from saga)
      toggleFreightP2PModal(displayOffer);
    }

    trackConversionInteraction(utfDataObject);
  }

  const COPY_ID =
    variationType === "WCCOfferCardCTA"
      ? COPY_ID_PREFIX
      : "WCCMultiBUOfferCardCTA";

  const renderCTA = (variant, href, content) => {
    return (
      <StyledLink
        dataTestid={`wcc-offer-card-primary-cta-${offerIndex}`}
        href={href}
        variant={variant}
        size={Sizes.md}
        clickEvent={onClickHandler}
        className={isHighlighted ? "highlighted" : ""}
        rel="follow"
        target="_blank"
      >
        {content}
      </StyledLink>
    );
  };

  const getButtonLink = () => {
    if (
      !displayOffer.pathToPurchaseLink &&
      displayOffer.specialOffers &&
      nullChecker(`${displayOffer.specialOffers}_Link`)
    ) {
      return getCopy(
        `${CARD_AND_SUMMARY_COPY_ID_PREFIX}.${displayOffer.specialOffers}_Link`
      );
    }

    return displayOffer.pathToPurchaseLink;
  };

  const getButtonText = () => {
    if (
      displayOffer.specialOffers &&
      nullChecker(`${displayOffer.specialOffers}_Button`)
    ) {
      return getCopy(
        `${CARD_AND_SUMMARY_COPY_ID_PREFIX}.${displayOffer.specialOffers}_Button`
      );
    }

    return getCopy(`${COPY_ID}.${purchaseMethod}_ctaPrimary`);
  };

  const renderPrimaryCTA = () => {
    return renderCTA("primary", getButtonLink(), getButtonText());
  };

  const renderSecondaryCTA = () => {
    if (
      !supportContactInformation ||
      !Object.keys(supportContactInformation).length
    ) {
      return renderPrimaryCTA(); // If supportContactInformation is empty, render primary CTA
    }

    const hasPhoneNumber =
      supportContactInformation.phoneNumbers &&
      supportContactInformation.phoneNumbers.length > 0;
    const hasWebsiteUrl = !!supportContactInformation.websiteUrl;

    if (!hasPhoneNumber && !hasWebsiteUrl) {
      return renderPrimaryCTA(); // If no phone numbers or website URL, render primary CTA. This is for when we have "email"
    }

    const linkHref = hasPhoneNumber
      ? `tel:${supportContactInformation.phoneNumbers[0]?.phoneNumber}`
      : supportContactInformation.websiteUrl;

    const content = hasPhoneNumber
      ? supportContactInformation.phoneNumbers[0]?.phoneNumber
      : getCopy(`${COPY_ID}.${purchaseMethod}_ctaPrimary`);

    return renderCTA(hasPhoneNumber ? "outline" : "primary", linkHref, content);
  };

  return (
    <React.Fragment>
      {(purchaseMethod === offerPurchaseMethods.CLICK ||
        purchaseMethod === offerPurchaseMethods.WALK) &&
        renderPrimaryCTA()}
      {purchaseMethod === offerPurchaseMethods.CALL && renderSecondaryCTA()}
    </React.Fragment>
  );
}

WCCOfferCardCTA.defaultProps = {
  variationType: "WCCOfferCardCTA",
  utfDataObject: {},
  loadExpressData: () => {},
  documentsOptionValue: "",
  toggleFreightP2PModal: () => {},
};

WCCOfferCardCTA.propTypes = {
  /** Internationalization library */
  intl: IntlPropType.isRequired,
  /** The index of the offer */
  offerIndex: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  displayOffer: PropTypes.object,
  isHighlighted: PropTypes.bool,
  /** The purchase method */
  purchaseMethod: PropTypes.oneOf(Object.values(offerPurchaseMethods))
    .isRequired,
  variationType: PropTypes.string,
  utfDataObject: PropTypes.object,
  canaryVersion: PropTypes.string,
  loadExpressData: PropTypes.func,
  documentsOptionValue: PropTypes.string,
  toggleFreightP2PModal: PropTypes.func,
};

const mapStateToProps = (state) => ({
  canaryVersion: state.canaryVersion.version,
  documentsOptionValue:
    state.tradeLane?.initialSet?.documentsOption?.value || "",
});

const mapDispatchToProps = (dispatch) => ({
  loadExpressData: (purchaseLink, packageType) =>
    dispatch(loadExpressDataRequest(purchaseLink, packageType)),
});

export default connect(mapStateToProps, mapDispatchToProps)(WCCOfferCardCTA);
